import { apiStandardSearch, apiGetFilterOptions, apiGetSearchFilterOptions, apiSearchResults } from '../api.ts'
import type { ISearchStore } from '../types.ts'
import type { ApiEndPointMappingKey } from '../types.ts'
import { STANDARDS_SEARCH_TAB, STANDARDS_RECORDS_PER_PAGE, OPENSEARCH_RESULT_WINDOW, SEARCH_IN_OPTIONS, STANDARDS_SEARCH_SORT_OPTIONS, STANDARDS_SEARCH_SORT_DEFAULT } from '../constants.ts';
import { getLocalLanguageISOCode } from '@/plugins/i18n.ts';
import { getFullICSCodeText } from '@/utils/storeHelpers.ts';
import { filterVisibleStatusOptions, getDefaultStatusOptions, getStatusDefaultOption, mapStatusOption, mergeStatusOptions } from './statusFacetHelpers.ts';
import { nextTick } from 'vue';
import { gaTrackUserSearch } from '@/utils/gaHelpers.ts';

const getStandardRecordsPerPage = (store: ISearchStore): number => {
  const storedValue =
    Number(store[`${STANDARDS_SEARCH_TAB}.resultData`]?.recordsPerPage) || STANDARDS_RECORDS_PER_PAGE
  return storedValue > 0 ? storedValue : STANDARDS_RECORDS_PER_PAGE
}

const clampPageToResultWindow = (pageNo: number, recordsPerPage: number): number => {
  if (recordsPerPage <= 0) return Math.max(1, pageNo)
  const maxAllowedPage = Math.max(1, Math.floor(OPENSEARCH_RESULT_WINDOW / recordsPerPage))
  if (!pageNo || pageNo < 1) return 1
  return Math.min(pageNo, maxAllowedPage)
}

const DEBUG_EXPLAIN_FLAG_KEY = '__SAI_STANDARDS_DEBUG_EXPLAIN__'
const ACTIVE_COLLECTION_OVERRIDE_KEY = '__SAI_STANDARDS_ACTIVE_COLLECTION_OVERRIDE__'

const isDebugExplainEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  return Boolean((window as any)[DEBUG_EXPLAIN_FLAG_KEY])
}

const getActiveCollectionOverride = (): string | null => {
  if (typeof window === 'undefined') return null
  const rawValue = (window as any)[ACTIVE_COLLECTION_OVERRIDE_KEY]
  const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return String(Math.trunc(numericValue))
  }
  return null
}

if (typeof window !== 'undefined') {
  ;(window as any).enableStandardsDebugExplain = (enabled = true) => {
    (window as any)[DEBUG_EXPLAIN_FLAG_KEY] = Boolean(enabled)
    const status = (window as any)[DEBUG_EXPLAIN_FLAG_KEY] ? 'enabled' : 'disabled'
    console.info(`[Standards Search] debugExplain ${status}`)
  }

  ;(window as any).setStandardsActiveCollectionOverride = (collectionId?: number | null) => {
    if (typeof collectionId === 'number' && Number.isFinite(collectionId) && collectionId > 0) {
      ;(window as any)[ACTIVE_COLLECTION_OVERRIDE_KEY] = Math.trunc(collectionId)
      console.info(
        `[Standards Search] activeCollectionId override set to ${(window as any)[ACTIVE_COLLECTION_OVERRIDE_KEY]}`,
      )
    } else {
      delete (window as any)[ACTIVE_COLLECTION_OVERRIDE_KEY]
      console.info('[Standards Search] activeCollectionId override cleared')
    }
  }
}

const normalizeParamsForComparison = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(normalizeParamsForComparison)
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc: Record<string, any>, key: string) => {
        acc[key] = normalizeParamsForComparison(value[key])
        return acc
      }, {})
  }
  return value
}

const getNormalizedParamsString = (rawParams: any): string | null => {
  if (rawParams === undefined || rawParams === null) return null
  const parsedParams =
    typeof rawParams === 'string'
      ? (() => {
          try {
            return JSON.parse(rawParams)
          } catch {
            return null
          }
        })()
      : rawParams

  if (!parsedParams || typeof parsedParams !== 'object') return null
  const paramsForNormalization = { ...parsedParams }

  // remove non-essential flags from comparison
  const ignoreFieldsForComparison = ['saveSearch', 'activeCollectionId', 'activeCollectionName', 'sub_id', 'group_id', 'esPreference'];
  for (const field of ignoreFieldsForComparison) {
    if (field in paramsForNormalization) {
      delete (paramsForNormalization as any)[field]
    }
  }

  // Treat equivalent values the same for comparison
  if ('filterby' in paramsForNormalization) {
    const filterbyValue = paramsForNormalization['filterby']
    if (filterbyValue === '' || filterbyValue === '0' || filterbyValue === 0) {
      paramsForNormalization['filterby'] = '0'
    }
  }
  if ('mainsearch' in paramsForNormalization) {
    const mainsearchValue = paramsForNormalization['mainsearch']
    if (mainsearchValue === '0' || mainsearchValue === 0 || mainsearchValue === '1' || mainsearchValue === 1) {
      paramsForNormalization['mainsearch'] = '0'
    }
  }

  const normalizedParams = normalizeParamsForComparison(paramsForNormalization)
  return JSON.stringify(normalizedParams)
}

const isStandardsSavedSearchMatch = (params: any, savedSearches: any[]): boolean => {
  const targetParamsString = getNormalizedParamsString(params)
  if (!targetParamsString) return false

  const savedSearchList = savedSearches || []
  for (const savedSearch of savedSearchList) {
    const savedParamsString = getNormalizedParamsString((savedSearch as any)?.params)
    if (!savedParamsString) {
      continue // skip invalid json_encode entries
    }
    if (savedParamsString === targetParamsString) {
      return true
    }
  }
  return false
}

export default {
  initStandardSearchBarFilterOptions: async function(this: ISearchStore) {
    let filterOptions: any = null;
    try {
      filterOptions = await apiGetSearchFilterOptions();
    } catch (error) {
      console.log('Standard bootstrap filter options failed, using fallback sources.', error);
    }

    const datasetOptions = Array.isArray(filterOptions?.filters?.dataset)
      ? filterOptions.filters.dataset.map((option: any) => ({
          id: option?.key ?? '',
          text: option?.value ?? '',
        }))
      : [];
    (this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.dataset`] = datasetOptions;

    const apiStatusOptions = Array.isArray(filterOptions?.filters?.status)
      ? filterOptions.filters.status.map((option: any) =>
          mapStatusOption({
            id: option?.key ?? '',
            value: option?.value ?? '',
          }),
        )
      : [];
    (this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.status`] =
      apiStatusOptions.length > 0 ? mergeStatusOptions(apiStatusOptions) : getDefaultStatusOptions();

    const fallbackFieldMappings: Record<string, ApiEndPointMappingKey> = {
      [`lookUpOptions.${STANDARDS_SEARCH_TAB}.publisher`]: `${STANDARDS_SEARCH_TAB}.publisher`,
      [`lookUpOptions.${STANDARDS_SEARCH_TAB}.country`]: `${STANDARDS_SEARCH_TAB}.country`,
    };

    const fallbackEntries = await Promise.all(
      Object.entries(fallbackFieldMappings).map(async ([lookupField, apiField]) => {
        let lookupOptions: any[] = [];
        try {
          const fallbackOptions = await apiGetFilterOptions(apiField);
          lookupOptions = Array.isArray(fallbackOptions) ? fallbackOptions : [];
        } catch (error) {
          console.log(`Standard filter fallback failed for ${apiField}.`, error);
        }

        if (lookupOptions.length === 0) {
          const apiKey = apiField.endsWith('.publisher') ? 'publisher' : 'country';
          const primaryOptions = filterOptions?.filters?.[apiKey];
          lookupOptions = Array.isArray(primaryOptions)
            ? primaryOptions.map((option: any) => ({
                id: option?.key ?? option?.id ?? '',
                text: option?.value ?? option?.text ?? option?.label ?? '',
              }))
            : [];
        }

        return [lookupField, lookupOptions] as const;
      }),
    );

    for (const [lookupField, options] of fallbackEntries) {
      (this as any)[lookupField] = options;
    }

    if (((this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.publisher`] || []).length === 0) {
      (this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.publisher`] = [
        ...((this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.publisher`] || []),
      ];
    }

    if (((this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.country`] || []).length === 0) {
      (this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.country`] = [
        ...((this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.country`] || []),
      ];
    }
  },
  resetStandardsFiltersForMainSearch: function(this: ISearchStore, facetFilter: boolean = false) {

    // reset all search bar filters except document number, if search results are displayed
    //@ts-ignore
    if (this.isResultLoaded(STANDARDS_SEARCH_TAB)) {
      this.clearSearchBarFilters(STANDARDS_SEARCH_TAB, ['documentNumber']);
      //set default filterby, if its main search and not hash search
      const isHashSearch = !!(this[`${STANDARDS_SEARCH_TAB}.documentNumber`]?.startsWith('#'));
      if (!facetFilter && !isHashSearch) {
        //set subscription default searchIn option
        const searchin = String((this as any)?.filterDefaults?.searchin ?? '0');
        const selectedOption =
          SEARCH_IN_OPTIONS.find(opt => opt.id === searchin) ??
          SEARCH_IN_OPTIONS.find(opt => opt.id === "0") ??
          SEARCH_IN_OPTIONS[0];
        this[`${STANDARDS_SEARCH_TAB}.searchIn`] = [selectedOption];

        //set subscription default sort option
        const rawSortDefault = String((this as any)?.filterDefaults?.sortby ?? STANDARDS_SEARCH_SORT_DEFAULT.id);
        const selectedSortOption =
          STANDARDS_SEARCH_SORT_OPTIONS.find(opt => opt.id === rawSortDefault) ??
          STANDARDS_SEARCH_SORT_OPTIONS.find(opt => opt.id === STANDARDS_SEARCH_SORT_DEFAULT.id) ??
          STANDARDS_SEARCH_SORT_DEFAULT;
        this[`${STANDARDS_SEARCH_TAB}.result.sort`] = [selectedSortOption];

        const selectedStatusOption = getStatusDefaultOption((this as any)?.filterDefaults?.status ?? '');
        this[`${STANDARDS_SEARCH_TAB}.status`] = selectedStatusOption ? [selectedStatusOption] : [];
      }
    }
    if(!facetFilter) {
      //if search from search bar reset all facet filters, apply only the search bar filters
      this.clearSearchFacetFilters(STANDARDS_SEARCH_TAB, ['documentNumber']);
    }
  },
  getStandardSearchParams: function(this: ISearchStore, facetFilter: boolean = false, pageNo: number = 1) {
    //reset facet filters if the search is performed from the main search bar
    this.resetStandardsFiltersForMainSearch(facetFilter);
    if(this[`${STANDARDS_SEARCH_TAB}.documentNumber`].trim() =='') {
      this[`${STANDARDS_SEARCH_TAB}.documentNumber`] = "*";
    }

    const recordsPerPage = getStandardRecordsPerPage(this);
    const debugExplainEnabled = isDebugExplainEnabled();
    const activeCollectionOverride = getActiveCollectionOverride();
    const safePageNo = clampPageToResultWindow(pageNo ?? 1, recordsPerPage);
    const activeCollectionId = activeCollectionOverride ?? (this['activeCollectionId'] || '');
    const params: Record<string, string | number | boolean> = {
      "filter-publisher": "",
      "filter-country": "",
      "filter-language": "",
      "filter-include": "",
      "filter-year": "",
      "filter-dataset": "",
      "filter-xref":  this[`${STANDARDS_SEARCH_TAB}.crossReference`].trim(),
      "filterby": "0",
      "index": "standards",
      "query_1": this[`${STANDARDS_SEARCH_TAB}.documentNumber`].trim(),
      "query_2": "",
      "query_5": this[`${STANDARDS_SEARCH_TAB}.crossReference`].trim(),
      "publisher": "",
      "current": "on",
      "include": "CUR",
      "exclude": "",
      "country": "",
      "dataset": "",
      "from_collection": "",
      "page": String(safePageNo),
      "orderBy": "",
      "updated": "",
      "sub_id": "",
      "group_id": "",
      "class": "a",
      "mainsearch": facetFilter ? 0 : 1,
      "activeCollectionId": activeCollectionId,
      "activeCollectionName": this['activeCollectionName'] || '',
      "icsCode": "",
      "icsCodeLevel1": "",
      "icsCodeLevel2": "",
      "icsCodeLevel3": "",
      "language": getLocalLanguageISOCode() || 'en'
    }

    const persistedEsPreference = String(this[`${STANDARDS_SEARCH_TAB}.resultData`]?.esPreference ?? '').trim();
    if (persistedEsPreference) {
      params['esPreference'] = persistedEsPreference;
    }

    //use search bar filter values
    let filterPrefix = `${STANDARDS_SEARCH_TAB}`;
    //if facetFilter is true, use the facetFilter values
    if(facetFilter) {
      filterPrefix = `${STANDARDS_SEARCH_TAB}.facetFilter`;
    }

    //set status
    const status =  (this as any)[filterPrefix + '.status'];
    const selectedStatus = [];
    if(status.length > 0) {
      for (const statusItem of status) {
          selectedStatus.push(statusItem.id);
      }
    }
    params['filter-include'] = selectedStatus.join(',');
    params['include'] = selectedStatus.join(',');

    //set publisher
    const publishers =  (this as any)[filterPrefix + '.publisher'];
    const selectedPublishers = [];
    if(publishers.length > 0) {
      for(const publisher of publishers) {
        selectedPublishers.push(publisher.id);
      }
    }
    params['filter-publisher'] = selectedPublishers.join(',');
    params['publisher'] = selectedPublishers.join(',');

    //set country
    const countries =  (this as any)[filterPrefix + '.country'];
    const selectedCountries = [];
    if(countries.length > 0) {
      for(const country of countries) {
        selectedCountries.push(country.id);
      }
    }
    params['filter-country'] = selectedCountries.join(',');
    params['country'] = selectedCountries.join(',');

    //set dataset
    const datasets =  (this as any)[filterPrefix + '.dataset'];
    const selectedDatasets = [];
    if(datasets.length > 0) {
      for(const dataset of datasets) {
        selectedDatasets.push(dataset.id);
      }
    }
    params['filter-dataset'] = selectedDatasets.join(',');
    params['dataset'] = selectedDatasets.join(',');

    //set years
    const years =  (this as any)[filterPrefix + '.year'];
    const selectedYears = [];
    if(years.length > 0) {
      for(const year of years) {
        selectedYears.push(year.id);
      }
    }
    params['filter-year'] = selectedYears.join(',');

    //set languages
    const langauages =  (this as any)[filterPrefix + '.language'];
    const selectedLanguages = [];
    if(langauages.length > 0) {
      for(const language of langauages) {
        selectedLanguages.push(language.id);
      }
    }
    params['filter-language'] = selectedLanguages.join(',');


    const searchIns =  (this as any)[filterPrefix + '.searchIn'];
    const selectedSearchIn = [];
    if(searchIns.length > 0) {
      for(const searchIn of searchIns) {
        selectedSearchIn.push(searchIn.id);
      }
    }
    params['filterby'] = selectedSearchIn.join(',');

    if(facetFilter) {
      params['filter-xref'] = this[`${STANDARDS_SEARCH_TAB}.facetFilter.crossReference`].trim();
    }
    params['orderBy'] = "0";
    if(this[`${STANDARDS_SEARCH_TAB}.result.sort`].length > 0) {
      params['orderBy'] = this[`${STANDARDS_SEARCH_TAB}.result.sort`][0].id;
    }

    params['icsCode'] = String(this[`${filterPrefix}.icsCode`] ?? '').trim();
    params['icsCodeLevel1'] = String(this[`${filterPrefix}.descriptorField`]?.map((item: any) => item.id).join(',') ?? '').trim();
    params['icsCodeLevel2'] = String(this[`${filterPrefix}.descriptorGroup`]?.map((item: any) => item.id).join(',') ?? '').trim();
    params['icsCodeLevel3'] = String(this[`${filterPrefix}.descriptorSubGroup`]?.map((item: any) => item.id).join(',') ?? '').trim();
    params['_clientRecordsPerPage'] = recordsPerPage;
    if (debugExplainEnabled) {
      params['debugExplain'] = true;
    }
    return params;
  },
  setStandardSearchResultData: function(this: ISearchStore, resultData: any) {
    this[`${STANDARDS_SEARCH_TAB}.resultData`] = resultData;
    const filters = resultData?.filters || {};
    //for each filters, set the lookup fields
    const facetFilterMapping: Record<string, string> = {
      'publisher': 'publisher',
      'include': 'status',
      'country': 'country',
      'language': 'language',
      'year': 'year',
      'filterby': 'searchIn',
      "icsLevel1": 'descriptorField',
      "icsLevel2": 'descriptorGroup',
      "icsLevel3": 'descriptorSubGroup'
    };

    for (const [apiKey, stateKey] of Object.entries(facetFilterMapping)) {
      //selected options from api response
      let apiReturnedSelectedOptions = filters[apiKey]?.filter((option: any) => option.selected).map((option: any) => ({
        id: String(option.id),
        text: `${option.value} ${option.count ? `(${option.count})` : ''}`.trim()
      })) || [];
      if (apiKey === 'include') {
        apiReturnedSelectedOptions = filterVisibleStatusOptions(
          apiReturnedSelectedOptions.map((option: any) => mapStatusOption(option, true)),
        );
      }

      let combinedSelectedOptions:any[] = [];
      //selected search bar options
      if(stateKey != 'searchIn') {
        const selectedSearchBarOptions = (this as any)[`${STANDARDS_SEARCH_TAB}.${stateKey}`] || [];

        //selected facet filter options
        let selectedFacetFilterOptions = (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${stateKey}`] || [];
        selectedFacetFilterOptions = selectedFacetFilterOptions.map((opt: any) => { //strip out count "(num)" from the facets selected options
          return { id: opt.id.toString(), text: opt.text.toString().replace(/\s*\(\s*\d+\s*\)\s*$/, '') };
        });

        //combine selected search bar and facet filter options
        //also remove duplicates from search bar selections
        combinedSelectedOptions = [...selectedFacetFilterOptions, ...selectedSearchBarOptions.filter((prevOpt: any) => {
          return !selectedFacetFilterOptions.some((selOpt: any) => selOpt.id.toString() === prevOpt.id.toString());
        })];
      }

      //remove duplicates from the (selected search bar + facet filter) selections
      //and combine selected options from api and (selected search bar + facet filter)
      //so we have selected options from api response + user selected options(search bar + facet filter) as api wlll not have all user selected options
      let selectedFilterOptions = [...apiReturnedSelectedOptions, ...combinedSelectedOptions.filter((prevOpt: any) => {
        return !apiReturnedSelectedOptions.some((selOpt: any) => selOpt.id.toString() === prevOpt.id.toString());
      })];
      if (apiKey === 'include') {
        selectedFilterOptions = filterVisibleStatusOptions(selectedFilterOptions);
      }
      (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${stateKey}`] = selectedFilterOptions;

      const finalSelectedFilterOptions = (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${stateKey}`] || [];
      if(apiKey == 'icsLevel1') {
        (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${stateKey}`] = getFullICSCodeText(finalSelectedFilterOptions, (this as any)['lookUpOptions.allDescriptorField']);
        this.loadICSCodeOptions({ field: `lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.descriptorGroup`, icsCode: finalSelectedFilterOptions, level: 2 })
      } else if(apiKey == 'icsLevel2') {
        (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${stateKey}`] = getFullICSCodeText(finalSelectedFilterOptions, (this as any)['lookUpOptions.allDescriptorGroup']);
        this.loadICSCodeOptions({ field: `lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.descriptorSubGroup`, icsCode: finalSelectedFilterOptions, level: 3 })
      } else if (apiKey =='icsLevel3') {
        (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${stateKey}`] = getFullICSCodeText(finalSelectedFilterOptions, (this as any)['lookUpOptions.allDescriptorSubGroup']);
      } else  { //for rest of the filters except icsLevel1, icsLevel2, icsLevel3, append count (num) to the text
        let options = filters[apiKey]?.map((option: any) => ({
          id: option?.id?.toString() ?? '',
          text: `${option?.value} ${option?.count ? `(${option?.count})` : ''}`.trim()
        })) || [];
        if (apiKey === 'include') {
          options = filterVisibleStatusOptions(
            (filters[apiKey] || []).map((option: any) => mapStatusOption(option, true)),
          );
        }
        if ((apiKey === 'publisher' || apiKey === 'country') && options.length === 0) {
          options = ((this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.${stateKey}`] || []).map((option: any) => ({
            id: option?.id?.toString() ?? '',
            text: option?.text?.toString() ?? '',
          }));
        }
        (this as any)[`lookUpOptions.${STANDARDS_SEARCH_TAB}.facetFilter.${stateKey}`] = options;
      }

    }
    //check filter-xref or check query_5
    if(resultData.initialQuery?.['filter-xref'] && resultData.initialQuery?.['filter-xref'] != '') {
      this[`${STANDARDS_SEARCH_TAB}.facetFilter.crossReference`] = resultData.initialQuery?.['filter-xref'];
    } else if(resultData.initialQuery?.['query_5'] && resultData.initialQuery?.['query_5'] != '') {
      this[`${STANDARDS_SEARCH_TAB}.facetFilter.crossReference`] = resultData.initialQuery?.['query_5'];
    } else {
      this[`${STANDARDS_SEARCH_TAB}.facetFilter.crossReference`] = filters['query_5']?.value ? filters['query_5']?.value : '';
    }
    if(resultData.initialQuery?.['query_1'] && resultData.initialQuery?.['query_1'] != '') {
      this[`${STANDARDS_SEARCH_TAB}.documentNumber`] = resultData.initialQuery?.['query_1'];
    }
    this[`${STANDARDS_SEARCH_TAB}.activeCollectionId`] =  resultData.initialQuery?.['active_collection_id'] ? resultData.initialQuery?.['active_collection_id'] : '';
    this[`${STANDARDS_SEARCH_TAB}.activeCollectionName`] =  resultData.initialQuery?.['active_collection_name'] ? resultData.initialQuery?.['active_collection_name'] : '';
    this[`${STANDARDS_SEARCH_TAB}.result.sort`] = [
      STANDARDS_SEARCH_SORT_OPTIONS.find(
        opt => opt.id === String(resultData.orderBy)
      ) ?? STANDARDS_SEARCH_SORT_DEFAULT
    ];
    const recordsPerPage = getStandardRecordsPerPage(this);
    const safePage = clampPageToResultWindow(resultData.page ?? 1, recordsPerPage);
    this[`${STANDARDS_SEARCH_TAB}.result.page`] = safePage;
  },
  async searchStandards(this: ISearchStore, facetFilter: boolean = false, pageNo: number = 1) {
    try {
      if(!facetFilter) {
        //need to toggle so that value propagate to isOpen watch in SaiDropdown
        this[`${STANDARDS_SEARCH_TAB}.facetFilter.searchIn.expanded`] = false;
        await nextTick();
        this[`${STANDARDS_SEARCH_TAB}.facetFilter.searchIn.expanded`] = true;
      }
      const params = this.getStandardSearchParams(facetFilter, pageNo);
      this.searchStandardsWithParams(params, true);
    } catch (e: any) {
      this[`${STANDARDS_SEARCH_TAB}.result.loading`] = false;
      console.log('Standard search failed');
    }
  },
  searchStandardsWithParams: async function(this: ISearchStore, params: any, handleRedirect: boolean = true) {
    try {
      this['searchResultActive'] = true;
      this[`${STANDARDS_SEARCH_TAB}.result.loading`] = true;
      const standardSearchApiAbortController = this.addApiAbortController('standardSearchApi');
      const resultData: any = await apiStandardSearch(params, standardSearchApiAbortController);
      this.setStandardSearchResultData(resultData);
      gaTrackUserSearch(STANDARDS_SEARCH_TAB, params, this[`${STANDARDS_SEARCH_TAB}.resultData`]);
      const existingIsSavedSearch = this[`${STANDARDS_SEARCH_TAB}.resultData`]?.isSavedSearch;
      const isSavedSearchMatch = existingIsSavedSearch
        ? true
        : isStandardsSavedSearchMatch(params, this['standards.savedSearches'] || []);
      this[`${STANDARDS_SEARCH_TAB}.resultData`].isSavedSearch = isSavedSearchMatch;
      this.saveSearchParamsToHistory(resultData?.searchContext, params); //need to make the product page back link work for standard search
      if(handleRedirect) {
        this.handleRedirect(resultData)
      } else {
        this[`${STANDARDS_SEARCH_TAB}.result.loading`] = false;
      }
    } catch (e: any) {
      if((e?.code??'') == 'ERR_CANCELED') return; //if the error is due to request cancellation, do nothing
      this[`${STANDARDS_SEARCH_TAB}.result.loading`] = false;
      console.log('Standard search with params failed');
    }
  },
  handleRedirect(this:ISearchStore, resultData: any) {
    if(resultData?.redirectTarget && resultData.redirectTarget == 'PublishedDocument') {
      window.location.href = `/management/display/index/0/${resultData?.redirectDocId}/-/${resultData?.searchContext}`;
    } else if(resultData?.redirectTarget && resultData.redirectTarget == 'DraftDocument') {
      window.location.href = `/management/displaydraft/index/0/${resultData?.redirectDocId}`;
    } else {
       this[`${STANDARDS_SEARCH_TAB}.result.loading`] = false;
    }
  },
  exportStandardSearchResults: async function(this: ISearchStore) {
    try {
      this[`${STANDARDS_SEARCH_TAB}.export.status`] = 'inprogress';
      let params = this.getStandardSearchParams(true, 1); //use facet filters for export
      params['export'] = '1';
      const standardSearchApiAbortController = this.addApiAbortController('standardSearchApi');
      const resultData: any = await apiStandardSearch(params, standardSearchApiAbortController);
      const docIds = resultData?.docIds || [];
      const exportDocIdS3Object = resultData?.exportDocIdS3Object || '';
      if(docIds.length == 0) {
          this[`${STANDARDS_SEARCH_TAB}.export.status`] = 'completewitherror';
          console.log('export collection search results failed. No documents found to export.');
          return;
      }
      //add export specific params
      const exportFormat = this[`${STANDARDS_SEARCH_TAB}.export.format`];
      const selectedFields = this[`${STANDARDS_SEARCH_TAB}.export.selectedFields`];
      let exportParams = {
        csvSeparator: exportFormat,
        fieldsToExport: selectedFields.join(','),
        exportDocIdS3Object: btoa(exportDocIdS3Object),
        nonce: this['nonce']
      };
      let results = await apiSearchResults(`exportDocs`, exportParams);
      if(results.status != 'error') {
          this[`${STANDARDS_SEARCH_TAB}.export.totalFound`] = resultData?.exportTotalFound;
          this[`${STANDARDS_SEARCH_TAB}.export.status`] = 'complete';
      } else if(results.status == 'error' && results.code == "401") { //if nolonger authenticated go to home page
          this[`${STANDARDS_SEARCH_TAB}.export.status`] = 'completewitherror';
          document.location = '/management/home/index';
      } else {
          this[`${STANDARDS_SEARCH_TAB}.export.status`] = 'completewitherror';
      }
    } catch(error) {
        this[`${STANDARDS_SEARCH_TAB}.export.status`] = 'completewitherror';
        console.log('export collection search results failed.');
        console.log(error);
    }
  }
}
